import React, { useEffect, useState, useMemo } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import LabelIcon from '@mui/icons-material/Label';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import LinkIcon from '@mui/icons-material/Link';
import LanguageIcon from '@mui/icons-material/Language';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import SendIcon from '@mui/icons-material/Send';
import Colors from '../../library/Colors';

const WebshopActionsScreen = (props) => {
    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop');
    }, []);

    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [resendId, setResendId] = useState('');
    const [resendEmail, setResendEmail] = useState('');
    const [resendWithBcc, setResendWithBcc] = useState(false);
    const [updateUrlModalOpen, setUpdateUrlModalOpen] = useState(false);
    const [newUrl, setNewUrl] = useState('');

    const isDark = props.theme === 'dark';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    const notify = (msg, severity = 1) => {
        if (props.notify) props.notify(msg, 4000, 'top-right', props.theme, severity);
    };

    // The webshop's `url` field on the auth backend was migrated as
    // CONCAT(cron_url, '/server/v1/mini-api'). The legacy scripts live at
    // `${cron_url}${path}`, so strip the mini-api suffix to get a clean base.
    const baseUrl = useMemo(() => {
        const url = props.selectedWebshop?.url || '';
        return url.replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/$/, '');
    }, [props.selectedWebshop?.url]);

    // Build a legacy URL with the standard cache-buster the legacy admin uses
    // (some endpoints, e.g. /admin/schedule-slug-reset, won't fire a fresh
    // job without it because the browser serves a cached response).
    const buildLegacyUrl = (path) => {
        const sep = path.includes('?') ? '&' : '?';
        return `${baseUrl}${path}${sep}__cb_=${Date.now()}`;
    };

    // Open a legacy storefront URL in a new tab. The user is normally already
    // logged into the legacy admin in another tab, so the script picks up
    // their session there. The script's progress output stays visible for
    // diagnostics, the MySft tab keeps its state.
    const openLegacyAction = (path) => {
        if (!baseUrl) { notify('No webshop selected', 1); return; }
        if (!path)    { notify('Action not wired yet', 1); return; }
        window.open(buildLegacyUrl(path), '_blank', 'noopener,noreferrer');
    };

    // Fire a legacy URL via a hidden iframe — same effect as opening a tab
    // but the user doesn't see a new browser window. Used for actions where
    // the script's response is just a status flag (e.g. {sent: true}) and
    // the user only needs a "completed" notification. Iframe.onload fires
    // when the script returns, regardless of HTTP status, so we treat that
    // as success — for finer-grained reporting we'd need CORS or a backend
    // proxy.
    const fireLegacyAction = (path, label) => {
        if (!baseUrl) { notify('No webshop selected', 1); return; }
        if (!path)    { notify('Action not wired yet', 1); return; }
        const url = buildLegacyUrl(path);

        notify(`${label}: running…`, 0);

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        let done = false;
        const finish = (ok, msg) => {
            if (done) return;
            done = true;
            notify(msg, ok ? 2 : 1);
            setTimeout(() => iframe.remove(), 100);
        };
        iframe.onload  = () => finish(true,  `${label}: completed`);
        iframe.onerror = () => finish(false, `${label}: failed`);
        // Safety net — if onload never fires (network hang), give up after 30s.
        setTimeout(() => finish(false, `${label}: timed out (no response)`), 30000);
        iframe.src = url;
        document.body.appendChild(iframe);
    };

    const resendTransaction = () => {
        if (!resendId.trim()) {
            notify('Reservation ID is required', 1);
            return;
        }
        // Email is optional — when omitted the storefront script falls back
        // to the recipient stored on the reservation.
        const params = new URLSearchParams({
            reservationId: resendId.trim(),
            withBcc:       resendWithBcc ? 'true' : 'false',
        });
        if (resendEmail.trim()) params.set('email', resendEmail.trim());
        // Per the storefront docs, both reservation and receipt resends go
        // through send-reservation-email.php. The receipt-only "gift voucher"
        // script is documented as "Do not use atm" in the storefront docs.
        // Fire via hidden iframe so the user gets a notify in MySft instead
        // of a tab full of "Get shipment type / Send before / Send done /
        // {sent: true}" debug output.
        fireLegacyAction(
            `/server/v1/scripts/send-reservation-email.php?${params.toString()}`,
            'Resend transaction'
        );
        setResendModalOpen(false);
        setResendId('');
        setResendEmail('');
    };

    const updateWebshopUrl = () => {
        if (!newUrl.trim()) {
            notify('Please enter a new URL', 1);
            return;
        }
        // No documented endpoint yet for changing the stored cron_url. Keep
        // the modal flow alive but surface a clear message until backend
        // support exists.
        notify('Update URL endpoint is not wired yet', 1);
        setUpdateUrlModalOpen(false);
        setNewUrl('');
    };

    // ── Action registry ────────────────────────────────────────────────────
    // path = legacy storefront URL relative to the webshop's cron_url.
    // Buttons with `path` open `${baseUrl}${path}` in a new tab; buttons
    // without are intentionally left unwired (no documented endpoint yet)
    // and render a disabled "Run" with a "Not wired yet" badge.
    const ACTIONS = [
        {
            id: 'attr-sync',
            icon: LabelIcon,
            title: 'Force attribute sync',
            description: 'Re-push all product attributes (size, color, material…) from the POS to the webshop.',
            path: '/server/v1/scripts/attributes/sync',
        },
        {
            id: 'brand-sync',
            icon: BrandingWatermarkIcon,
            title: 'Force brand logo sync',
            description: 'Re-upload every brand logo to the webshop from the POS catalog.',
            path: '/server/v1/crons/brand_logo_sync.php',
        },
        {
            id: 'full-product-sync',
            icon: Inventory2Icon,
            title: 'Force full product sync',
            description: 'Rebuild the entire webshop product catalog — prices, stock, descriptions, everything.',
            path: '/server/v1/scripts/products/sync',
        },
        {
            id: 'image-sync',
            icon: PhotoLibraryIcon,
            title: 'Force image sync',
            description: 'Re-push all product images to the webshop. Use when product images appear broken or missing.',
            path: '/server/v1/scripts/images/process',
        },
        {
            id: 'reservation-sync',
            icon: ReceiptLongIcon,
            title: 'Process queued reservations',
            description: 'Re-send pending reservations and receipts that failed to reach the webshop.',
            path: '/server/v1/crons/process_queued_reservations.php',
        },
        {
            id: 'resend-transaction',
            icon: ForwardToInboxIcon,
            title: 'Resend reservation / receipt',
            description: 'Manually resend a specific transaction email. Useful for testing template changes or replacing a wrong email address.',
            onClick: () => setResendModalOpen(true),
        },
        {
            id: 'reset-images',
            icon: RestorePageIcon,
            title: 'Reset images',
            description: 'Move product images back to the upload folder. They will be re-processed on the next sync.',
            destructive: true,
            // No documented endpoint yet.
        },
        {
            id: 'slug-reset',
            icon: LinkIcon,
            title: 'Slug reset',
            description: 'Regenerate the URL slugs for every product. Existing customer bookmarks may break.',
            destructive: true,
            path: `/server/v1/admin/schedule-slug-reset?lng=${props.lang || 'nl'}`,
        },
        {
            id: 'update-url',
            icon: LanguageIcon,
            title: 'Update webshop URL',
            description: 'Push the current shop domain configuration to the webshop server.',
            destructive: true,
            onClick: () => setUpdateUrlModalOpen(true),
            // No documented endpoint yet.
        },
        {
            id: 'update-version',
            icon: SystemUpdateAltIcon,
            title: 'Update webshop version',
            description: 'Upgrade the webshop software to the latest release. The shop may be offline briefly.',
            destructive: true,
            path: '/server/v1/scripts/application/update',
        },
    ];

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>
                    Webshop actions
                </Typography>
                <Typography variant="body2" sx={{ color: mutedText }}>
                    Admin actions that trigger server-side syncs or maintenance jobs. Destructive actions are marked in red.
                </Typography>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                gap: 1.5,
            }}>
                {ACTIONS.map((action) => {
                    const Icon = action.icon;
                    // An action is "wired" if it has a path or a custom onClick.
                    // Cards that have neither stay visible (so we don't lose the
                    // intent) but the Run button is disabled with a hint.
                    const wired = !!(action.path || action.onClick);
                    const disabled = !props.selectedWebshop || !wired;
                    return (
                        <Paper
                            key={action.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${borderColor}`,
                                backgroundColor: cardBg,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                transition: 'box-shadow 150ms, transform 150ms, border-color 150ms',
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                    transform: 'translateY(-2px)',
                                    borderColor: action.destructive ? Colors.def_red : Colors.sft_blue,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 1.25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: action.destructive
                                        ? (isDark ? 'rgba(255,82,82,0.12)' : 'rgba(255,82,82,0.08)')
                                        : (isDark ? 'rgba(25,118,210,0.12)' : 'rgba(25,118,210,0.08)'),
                                    color: action.destructive ? Colors.def_red : Colors.sft_blue,
                                    '& svg': { fontSize: 20 },
                                }}>
                                    <Icon />
                                </Box>
                                {!wired && (
                                    <Chip label="Not wired yet" size="small" variant="outlined" sx={{ ml: 'auto' }} />
                                )}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColor, fontSize: '0.9375rem' }}>
                                {action.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: mutedText, flexGrow: 1, lineHeight: 1.45, fontSize: '0.85rem' }}>
                                {action.description}
                            </Typography>
                            <Tooltip title={!props.selectedWebshop ? 'Select a webshop first' : (!wired ? 'No backend endpoint yet' : '')}>
                                <span style={{ alignSelf: 'flex-start' }}>
                                    <Button
                                        size="small"
                                        variant={action.destructive ? 'outlined' : 'contained'}
                                        color={action.destructive ? 'error' : 'primary'}
                                        disabled={disabled}
                                        onClick={() => {
                                            if (action.onClick) action.onClick();
                                            else openLegacyAction(action.path);
                                        }}
                                        sx={{ mt: 0.5 }}
                                    >
                                        Run
                                    </Button>
                                </span>
                            </Tooltip>
                        </Paper>
                    );
                })}
            </Box>

            <Dialog
                open={resendModalOpen}
                onClose={() => setResendModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Resend reservation / receipt
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        Opens the storefront's send-reservation-email script in a new tab. Useful for testing template changes or replacing a wrong recipient.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label="Reservation / receipt ID"
                            size="small"
                            fullWidth
                            value={resendId}
                            onChange={(e) => setResendId(e.target.value)}
                            autoFocus
                        />
                        <TextField
                            label="Recipient email (optional)"
                            size="small"
                            type="email"
                            fullWidth
                            value={resendEmail}
                            onChange={(e) => setResendEmail(e.target.value)}
                            placeholder="customer@example.com"
                            helperText="Leave empty to use the recipient stored on the reservation."
                        />
                        <FormControlLabel
                            control={
                                <Switch color="success"
                                    checked={resendWithBcc}
                                    onChange={(e) => setResendWithBcc(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: textColor }}>
                                    Include BCC recipients
                                </Typography>
                            }
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResendModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" startIcon={<SendIcon />} onClick={resendTransaction}>
                        Resend
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={updateUrlModalOpen}
                onClose={() => setUpdateUrlModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Update webshop URL
                    </Typography>
                    <Typography variant="body2" sx={{ color: mutedText, mt: 0.5 }}>
                        Change the domain the webshop is served from. Existing links will redirect.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            label="Current URL"
                            size="small"
                            fullWidth
                            value={baseUrl}
                            disabled
                        />
                        <TextField
                            label="New URL"
                            size="small"
                            fullWidth
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            placeholder="https://shop.example.com"
                            autoFocus
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setUpdateUrlModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={updateWebshopUrl}>
                        Update URL
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default WebshopActionsScreen;
